import { Component, effect, inject } from '@angular/core';
import { NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { TranslatePipe } from './core/i18n/translate.pipe';
import { partnerIdForOrg } from '@crewdle/white-label';
import { WhiteLabelService } from '@crewdle/white-label/angular';
import { SupportChatbotComponent } from './components/support-chatbot/support-chatbot.component';
import { HomeHubOverlayComponent } from './components/home-hub-overlay/home-hub-overlay.component';
import { HomeHubOverlayService } from './components/home-hub-overlay/home-hub-overlay.service';
import { AuthService } from './core/services/auth.service';
import { AppUpdateService } from './core/services/app-update.service';
import { isChunkLoadError, reloadOnceForChunkError, registerChunkErrorRecovery } from './core/chunk-reload';
import { OrgContextService } from './core/services/org-context.service';
import { PhantomAuthService } from './core/services/phantom-auth.service';
import { ThemeService } from './core/services/theme.service';
import { TranslateService } from './core/i18n/translate.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, SupportChatbotComponent, HomeHubOverlayComponent, TranslatePipe],
  template: `
    <router-outlet /><app-support-chatbot />
    <app-home-hub-overlay [open]="hubOverlay.open()" (closed)="hubOverlay.hide()" />

    <!-- A newer deploy is live but this tab hasn't navigated (navigation
         auto-reloads): give the parked tab a visible way onto the new build. -->
    @if (appUpdate.updateAvailable()) {
      <div
        class="fixed bottom-4 left-1/2 z-[300] flex w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-xl border border-border bg-surface px-4 py-2 shadow-lg"
        role="status"
      >
        <span class="min-w-0 text-xs text-text">{{ 'app.update-available' | t }}</span>
        <button
          type="button"
          class="shrink-0 rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-primary-700 cursor-pointer"
          (click)="reloadForUpdate()"
        >
          {{ 'app.update-refresh' | t }}
        </button>
      </div>
    }
  `,
  styles: [`:host { display: block; height: 100%; }`],
})
export class App {
  readonly hubOverlay = inject(HomeHubOverlayService);
  readonly appUpdate = inject(AppUpdateService);
  private auth = inject(AuthService);
  private phantomAuth = inject(PhantomAuthService);
  private router = inject(Router);
  private theme = inject(ThemeService);
  private translate = inject(TranslateService);
  private orgContext = inject(OrgContextService);
  private whiteLabel = inject(WhiteLabelService);

  constructor() {
    // Recover from post-deploy chunk-load failures that happen outside the
    // router (dynamic imports, deferred views).
    registerChunkErrorRecovery();

    this.phantomAuth.start();

    effect(() => {
      const user = this.auth.currentUser();
      if (user) {
        this.theme.loadUserTheme();
        this.translate.loadUserLanguage();
      }
    });

    // Feed org changes to the white-label service. Only once an org is present:
    // org context resolves async after auth, and propagating the transient null
    // would clear the cached pre-paint brand mid-boot. Switching to a
    // partner-less org still clears (org non-null, partnerIdForOrg → null).
    effect(() => {
      const org = this.orgContext.currentOrg();
      if (org) this.whiteLabel.setPartnerId(partnerIdForOrg(org));
    });

    // Hold the index.html loader until branding is decided (partner or
    // Crewdle) so the chrome never flips brands in front of the user.
    // settled() is time-capped, so a slow read can't trap anyone here.
    void this.whiteLabel.settled().then(() => {
      document.getElementById('splash-screen')?.remove();
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && this.appUpdate.updateAvailable()) {
        location.reload();
        return;
      }
      if (event instanceof NavigationError && isChunkLoadError(event.error)) {
        reloadOnceForChunkError();
      }
    });
  }

  reloadForUpdate(): void {
    location.reload();
  }
}
